import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";

const API = "/api";

const ESTADOS = ["pendiente", "confirmado", "completado", "cancelado"];
const COBERTURAS = ["Galeno", "Particular", "Urgencias", "Medicus", "Omint", "OSDE", "Swiss Medical", "IOMA", "PAMI", "Otra"];
const SERVICIOS_GENERALES = ["Consulta general", "Control", "Procedimiento", "Estudio", "Limpieza", "Otro"];
const SERVICIOS_URGENCIAS = [
  "Tratamiento de conducto (Endodoncia)",
  "Drenaje de absceso dental (Flemón)",
  "Extracción dental (Exodoncia)",
  "Reparación de fracturas dentales",
  "Tratamiento de Alveolitis",
  "Cementado de coronas o puentes caídos",
  "Tratamiento de Pericoronaritis (Muela del juicio)",
  "Sutura de tejidos blandos (labios o lengua)",
  "Ajuste de ortodoncia por lesiones",
  "Consulta General",
];

// Convert DD/MM/YYYY → YYYY-MM-DD for input[type=date]
const toInputDate = (val) => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    const [d, m, y] = val.split("/");
    return `${y}-${m}-${d}`;
  }
  return val;
};

// Convert YYYY-MM-DD → DD/MM/YYYY for Supabase storage
const toSupabaseDate = (val) => {
  if (!val) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split("-");
    return `${d}/${m}/${y}`;
  }
  return val;
};

const initialForm = {
  nombre: "",
  apellido: "",
  dni: "",
  telefono: "",
  email: "",
  direccion: "",
  cp: "",
  cobertura: "",
  credencial: "",
  servicio: "",
  fecha_turno: "",
  hora_turno: "",
  estado: "pendiente",
  pago_completado: false,
  monto_pago: "",
};

// Time slots 08:00–20:30 in 30-min increments
const HORA_SLOTS = [];
for (let h = 8; h <= 20; h++) {
  HORA_SLOTS.push(`${String(h).padStart(2, "0")}:00`);
  HORA_SLOTS.push(`${String(h).padStart(2, "0")}:30`);
}
// Result: 08:00 … 20:30 (26 slots)

export default function NuevoTurnoModal({ isOpen, onClose, onSuccess, turnoEdit = null, initialDate = null }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [disponWarnings, setDisponWarnings] = useState([]);
  const isEdit = !!turnoEdit;

  // Check availability informatively (never blocks submission)
  const checkDisponibilidad = useCallback(async (fecha, hora) => {
    if (!fecha) { setDisponWarnings([]); return; }
    try {
      const resp = await axios.get(`${API}/disponibilidad?fecha=${fecha}`, { headers: getAuthHeader() });
      const dia = resp.data?.[0];
      if (!dia) { setDisponWarnings([]); return; }
      const warnings = [];
      if (dia.es_feriado) warnings.push(`Día marcado como feriado${dia.feriado_motivo ? `: ${dia.feriado_motivo}` : ''}`);
      else if (!dia.activo) warnings.push('Día marcado como no laboral en la planificación');
      else if (hora && dia.franjas_horarias?.length > 0) {
        const [h, m] = hora.split(':').map(Number);
        const mins = h * 60 + m;
        const dentroFranja = dia.franjas_horarias.some(
          (f) => {
            const [hi, mi] = f.inicio.split(':').map(Number);
            const [hf, mf] = f.fin.split(':').map(Number);
            return mins >= hi * 60 + mi && mins < hf * 60 + mf;
          }
        );
        if (!dentroFranja) warnings.push('Horario fuera de las franjas configuradas');
      }
      setDisponWarnings(warnings);
    } catch { setDisponWarnings([]); }
  }, [getAuthHeader, isEdit]);

  useEffect(() => {
    if (turnoEdit) {
      setForm({
        ...initialForm,
        ...turnoEdit,
        fecha_turno: toInputDate(turnoEdit.fecha_turno),
        monto_pago: turnoEdit.monto_pago ?? "",
      });
    } else {
      setForm({ ...initialForm, fecha_turno: initialDate || "" });
    }
    setDisponWarnings([]);
  }, [turnoEdit, isOpen]);

  useEffect(() => {
    checkDisponibilidad(form.fecha_turno, form.hora_turno);
  }, [form.fecha_turno, form.hora_turno, checkDisponibilidad]);

  if (!isOpen) return null;

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.hora_turno) {
      toast.error("Seleccioná un horario");
      return;
    }
    setLoading(true);
    try {
      const data = {
        ...form,
        fecha_turno: toSupabaseDate(form.fecha_turno),
        monto_pago: form.monto_pago ? parseFloat(form.monto_pago) : null,
      };
      // Remove empty strings
      Object.keys(data).forEach((k) => { if (data[k] === "") data[k] = null; });

      if (isEdit) {
        await axios.patch(`${API}/turnos/${turnoEdit.id}`, data, { headers: getAuthHeader() });
        toast.success("Turno actualizado correctamente");
      } else {
        await axios.post(`${API}/turnos`, data, { headers: getAuthHeader() });
        toast.success("Turno creado correctamente");
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el turno");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-900">
            {isEdit ? "Editar turno" : "Nuevo turno"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            data-testid="modal-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Datos del paciente */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Datos del paciente</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Nombre *</label>
                <input className="input-field" value={form.nombre || ""} onChange={(e) => set("nombre", e.target.value)} required data-testid="input-nombre" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Apellido *</label>
                <input className="input-field" value={form.apellido || ""} onChange={(e) => set("apellido", e.target.value)} required data-testid="input-apellido" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">DNI</label>
                <input className="input-field" value={form.dni || ""} onChange={(e) => set("dni", e.target.value)} data-testid="input-dni" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Teléfono</label>
                <input className="input-field" value={form.telefono || ""} onChange={(e) => set("telefono", e.target.value)} data-testid="input-telefono" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-slate-500 mb-1 block">Email</label>
                <input type="email" className="input-field" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
              </div>
            </div>
          </div>

          {/* Cobertura */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Cobertura</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Obra social</label>
                <select className="input-field" value={form.cobertura || ""} onChange={(e) => {
                  const newCobertura = e.target.value;
                  const isUrgencias = newCobertura === "Urgencias";
                  const validList = isUrgencias ? SERVICIOS_URGENCIAS : SERVICIOS_GENERALES;
                  setForm((f) => ({
                    ...f,
                    cobertura: newCobertura,
                    credencial: newCobertura === "Particular" ? "" : f.credencial,
                    servicio: f.servicio && validList.includes(f.servicio) ? f.servicio : "",
                  }));
                }} data-testid="select-cobertura">
                  <option value="">Sin especificar</option>
                  {COBERTURAS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">
                  Credencial
                  {form.cobertura === "Particular" && (
                    <span className="ml-1 text-slate-400">(no aplica)</span>
                  )}
                </label>
                <input
                  className={`input-field ${form.cobertura === "Particular" ? "bg-slate-100 text-slate-400 cursor-not-allowed" : ""}`}
                  value={form.credencial || ""}
                  onChange={(e) => set("credencial", e.target.value)}
                  disabled={form.cobertura === "Particular"}
                  placeholder={form.cobertura === "Particular" ? "—" : ""}
                />
              </div>
            </div>
          </div>

          {/* Turno */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Datos del turno</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Fecha *</label>
                <input type="date" className="input-field" value={form.fecha_turno || ""} onChange={(e) => set("fecha_turno", e.target.value)} required data-testid="input-fecha" />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label className="text-xs text-slate-500 mb-2 block">
                  Hora *{form.hora_turno && <span className="ml-2 font-semibold text-blue-600">{form.hora_turno}</span>}
                </label>
                <div className="grid grid-cols-6 gap-1">
                  {HORA_SLOTS.map((hora) => (
                    <button
                      key={hora}
                      type="button"
                      onClick={() => set("hora_turno", hora)}
                      data-testid={`time-slot-${hora}`}
                      className={`py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        form.hora_turno === hora
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50"
                      }`}
                    >
                      {hora}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Servicio</label>
                <select className="input-field" value={form.servicio || ""} onChange={(e) => set("servicio", e.target.value)} data-testid="select-servicio">
                  <option value="">Seleccionar</option>
                  {(form.cobertura === "Urgencias" ? SERVICIOS_URGENCIAS : SERVICIOS_GENERALES).map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Estado</label>
                <select className="input-field" value={form.estado || "pendiente"} onChange={(e) => set("estado", e.target.value)} data-testid="select-estado">
                  {ESTADOS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Pago */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Pago</p>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="pago_completado"
                  checked={form.pago_completado}
                  onChange={(e) => set("pago_completado", e.target.checked)}
                  className="w-4 h-4 accent-blue-600"
                  data-testid="checkbox-pago"
                />
                <label htmlFor="pago_completado" className="text-sm text-slate-700">Pago completado</label>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Monto ($)</label>
                <input type="number" className="input-field" value={form.monto_pago || ""} onChange={(e) => set("monto_pago", e.target.value)} placeholder="0.00" step="0.01" data-testid="input-monto" />
              </div>
            </div>
          </div>

          {/* Availability warning — informative only, never blocks */}
          {disponWarnings.length > 0 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <AlertTriangle size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                {disponWarnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700">{w}</p>
                ))}
                <p className="text-xs text-amber-500 mt-1">El turno se puede crear igual.</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="submit-turno-btn">
              {loading ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear turno"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
